/**
 * ResultsScreen - Quiz Results and Summary Screen
 * 
 * Displays final quiz results including score, ranking, and question review.
 * 
 * Features:
 * - Final score display
 * - Leaderboard/ranking
 * - Question-by-question review
 * - Performance statistics
 * - Share/download options
 * - Dark mode support
 */

import React, { useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeToggle } from '../../../context/ThemeContext';
import { ScoreBadge, StreakBadge } from '../components/ScoreDisplay';
import { QuestionCardCompact } from '../components/QuestionCard';

/**
 * ResultsScreen component
 * @param {Object} settings - Quiz settings
 * @param {Array} questions - All quiz questions
 * @param {Object} answersMap - Map of question index to answer data
 * @param {number} totalScore - Final total score
 * @param {number} maxStreak - Maximum streak achieved
 * @param {Array} participants - All participants (for leaderboard)
 * @param {string} currentParticipantId - Current participant's ID
 * @param {string} currentParticipantName - Current participant's name
 * @param {Function} onPlayAgain - Callback to play again
 * @param {Function} onLeave - Callback to leave
 */
export function ResultsScreen({
  settings = {},
  questions = [],
  answersMap = {},
  totalScore = 0,
  maxStreak = 0,
  participants = [],
  currentParticipantId,
  currentParticipantName,
  onPlayAgain,
  onLeave,
}) {
  const { darkMode } = useTheme();
  const [showReview, setShowReview] = useState(false);

  // Calculate statistics
  const answeredQuestions = Object.keys(answersMap).length;
  const correctAnswers = Object.values(answersMap).filter(a => a?.isCorrect).length;
  const accuracy = answeredQuestions > 0 
    ? Math.round((correctAnswers / answeredQuestions) * 100) 
    : 0;

  // Sort participants by score for leaderboard
  // Handle both array and object format from Firebase
  const participantsList = Array.isArray(participants) 
    ? participants 
    : participants && typeof participants === 'object'
      ? Object.entries(participants).map(([id, data]) => ({ id, ...data }))
      : [];
  const sortedParticipants = [...participantsList].sort((a, b) => 
    (b.score || 0) - (a.score || 0)
  );

  // Find current user's rank
  const currentRank = sortedParticipants.findIndex(p => p.id === currentParticipantId) + 1;

  // Get medal for rank
  const getMedal = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '';
    }
  };

  // Get performance message
  const getPerformanceMessage = () => {
    if (accuracy >= 90) return { text: 'Hervorragend!', emoji: '🏆' };
    if (accuracy >= 75) return { text: 'Sehr gut!', emoji: '🌟' };
    if (accuracy >= 60) return { text: 'Gut gemacht!', emoji: '👍' };
    if (accuracy >= 40) return { text: 'Nicht schlecht!', emoji: '💪' };
    return { text: 'Weiter üben!', emoji: '📚' };
  };

  const performance = getPerformanceMessage();

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header
        className={`
          py-4 px-6 flex items-center justify-between
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border-b
        `}
      >
        <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Quiz beendet
        </h1>
        <ThemeToggle />
      </header>

      {/* Main Content */}
      <main className="flex-1 py-8 px-4 overflow-y-auto">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Performance Card */}
          <div
            className={`
              p-8 rounded-2xl text-center shadow-xl
              ${darkMode ? 'bg-gray-800' : 'bg-white'}
            `}
          >
            <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {performance.text}
            </h2>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {currentParticipantName}
            </p>

            {/* Rank Badge */}
            {currentRank > 0 && participants.length > 1 && (
              <div className="mt-4">
                <span
                  className={`
                    inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-bold
                    ${currentRank === 1
                      ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white'
                      : currentRank === 2
                        ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800'
                        : currentRank === 3
                          ? 'bg-gradient-to-r from-amber-600 to-amber-800 text-white'
                          : darkMode
                            ? 'bg-gray-700 text-gray-300'
                            : 'bg-gray-200 text-gray-700'
                    }
                  `}
                >
                  {getMedal(currentRank)} Platz {currentRank} von {participants.length}
                </span>
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Score */}
            <div
              className={`
                p-4 rounded-xl text-center
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
                shadow-lg
              `}
            >
              <p className={`text-3xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                {totalScore.toLocaleString()}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Punkte
              </p>
            </div>

            {/* Correct Answers */}
            <div
              className={`
                p-4 rounded-xl text-center
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
                shadow-lg
              `}
            >
              <p className={`text-3xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                {correctAnswers}/{questions.length}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Richtig
              </p>
            </div>

            {/* Accuracy */}
            <div
              className={`
                p-4 rounded-xl text-center
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
                shadow-lg
              `}
            >
              <p className={`text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {accuracy}%
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Genauigkeit
              </p>
            </div>

            {/* Max Streak */}
            <div
              className={`
                p-4 rounded-xl text-center
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
                shadow-lg
              `}
            >
              <p className={`text-3xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                {maxStreak}x
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Max Streak
              </p>
            </div>
          </div>

          {/* Leaderboard */}
          {participants.length > 1 && (
            <div
              className={`
                p-6 rounded-2xl shadow-lg
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
              `}
            >
              <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Rangliste
              </h3>

              <div className="space-y-2">
                {sortedParticipants.slice(0, 10).map((participant, index) => {
                  const rank = index + 1;
                  const isCurrentUser = participant.id === currentParticipantId;

                  return (
                    <div
                      key={participant.id || index}
                      className={`
                        flex items-center gap-3 p-3 rounded-lg
                        ${isCurrentUser
                          ? darkMode
                            ? 'bg-indigo-900/30 ring-2 ring-indigo-500'
                            : 'bg-indigo-50 ring-2 ring-indigo-500'
                          : darkMode
                            ? 'bg-gray-700/50'
                            : 'bg-gray-50'
                        }
                      `}
                    >
                      {/* Rank */}
                      <span
                        className={`
                          w-8 h-8 rounded-full flex items-center justify-center font-bold
                          ${rank === 1
                            ? 'bg-yellow-500 text-white'
                            : rank === 2
                              ? 'bg-gray-400 text-white'
                              : rank === 3
                                ? 'bg-amber-700 text-white'
                                : darkMode
                                  ? 'bg-gray-600 text-gray-300'
                                  : 'bg-gray-200 text-gray-600'
                          }
                        `}
                      >
                        {getMedal(rank) || rank}
                      </span>

                      {/* Name */}
                      <span
                        className={`
                          flex-grow font-medium
                          ${darkMode ? 'text-gray-200' : 'text-gray-800'}
                          ${isCurrentUser ? 'font-bold' : ''}
                        `}
                      >
                        {participant.name || `Teilnehmer ${index + 1}`}
                        {isCurrentUser && ' (Du)'}
                      </span>

                      {/* Score */}
                      <ScoreBadge score={participant.score || 0} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Question Review Toggle */}
          <button
            onClick={() => setShowReview(!showReview)}
            className={`
              w-full p-4 rounded-xl font-medium
              ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'}
              shadow-lg theme-transition
              flex items-center justify-between
            `}
          >
            <span>📋 Fragen überprüfen</span>
            <svg
              className={`w-5 h-5 transform transition-transform ${showReview ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Question Review List */}
          {showReview && (
            <div
              className={`
                p-6 rounded-2xl shadow-lg space-y-3
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
              `}
            >
              {questions.map((question, index) => {
                const answerData = answersMap[index];
                const selectedAnswer = answerData?.selectedOptions;
                const isCorrect = answerData?.isCorrect;

                return (
                  <QuestionCardCompact
                    key={index}
                    question={question}
                    index={index}
                    selectedAnswer={Array.isArray(selectedAnswer) 
                      ? selectedAnswer 
                      : selectedAnswer !== undefined 
                        ? selectedAnswer 
                        : null
                    }
                    isCorrect={isCorrect}
                  />
                );
              })}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            {onPlayAgain && (
              <button
                onClick={onPlayAgain}
                className={`
                  flex-1 py-4 px-6 rounded-xl font-semibold text-lg
                  bg-indigo-600 text-white hover:bg-indigo-700
                  transition-all duration-200 active:scale-[0.98]
                  flex items-center justify-center gap-2
                `}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Nochmal spielen
              </button>
            )}
            
            {onLeave && (
              <button
                onClick={onLeave}
                className={`
                  flex-1 py-4 px-6 rounded-xl font-semibold text-lg
                  ${darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
                  transition-all duration-200 active:scale-[0.98]
                  flex items-center justify-center gap-2
                `}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Beenden
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={`py-4 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Vielen Dank fürs Mitmachen!
      </footer>
    </div>
  );
}

export default ResultsScreen;
